import {
  VideoRecorder,
  VideoRecorderProps,
  VideoRecording,
} from "@amzn/itsvideo-recorder";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { RefObject, useContext, useState } from "react";
import { useIntl } from "react-intl";

import { Logger } from "@/apis/Logger";
import { Metrics } from "@/apis/Metrics";
import SubmitVideoButton from "@/components/Commons/SubmitVideoButton";
import { RefPageValue } from "@/components/GetSupport/ChatWithITSupport/types";
import { SnowplowTracker } from "@/helpers/snowplow";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  useFeatureFlag,
  useFeatureFlagsContext,
} from "@/stores/contexts/FeatureFlagsContext";
import { Day1EventCategory } from "@/types/snowplow-events";

import RecorderError from "./RecorderError";

type VideoRecorderCustomProps = Omit<
  VideoRecorderProps,
  "onRecord" | "onRetry" | "children" | "retryI18nId" | "resolutionProfile"
> & {
  /*** The Snowplow event category ***/
  eventCategory: Day1EventCategory;

  /**
   * For full remote ID verification only (FRIV):
   * Callback used to navigate the client back to the main ID verification steps on clicking
   * the button to submit for review at the end of each record step
   */
  clearSelectedStep?: () => void;

  /**
   * The reference to the FullIdVerification container element used to calculate the window
   * coordinate to scroll back to after the client completes ID recording steps.
   */
  fullIdVerificationElementRef?: RefObject<HTMLDivElement>;

  /**
   * Who mounted this component? Used for metrics collection on sub components.
   */
  refPage?: RefPageValue;
};

const VIDEO_RECORDER_ERROR_PUBLISHER =
  Metrics.publisherInstance.newChildActionPublisherForMethod(
    "ManualIdvVideoRecorder"
  );

export default function VideoRecorderCustom(
  props: VideoRecorderCustomProps
): JSX.Element {
  const { isLoading } = useFeatureFlagsContext();
  const is1080pCapEnabled = useFeatureFlag("cameraResolution1080pCap", true);

  if (isLoading) {
    return (
      <div data-test-id="videoRecorderDataTestId">
        <Spinner data-test-id="videoRecorderFlagsLoadingSpinner" />
      </div>
    );
  }

  return (
    <VideoRecorderCustomInner
      {...props}
      initialIs1080pCap={is1080pCapEnabled}
    />
  );
}

function VideoRecorderCustomInner({
  initialIs1080pCap,
  ...props
}: VideoRecorderCustomProps & { initialIs1080pCap: boolean }): JSX.Element {
  /**
   * Freeze the experiment assignment at mount: react-video-recorder does not restart an
   * active stream when its constraints prop changes, so a mid-session flag refetch flipping
   * the value would desynchronize the logged profile from the actual capture. Freezing
   * guarantees logged assignment === applied constraints for the whole recorder lifetime.
   */
  const [resolutionProfile] = useState<"1080p" | "max">(
    initialIs1080pCap ? "1080p" : "max"
  );
  const { state, dispatch } = useContext(AppContext);
  const retryRecording = () => dispatch({ type: ActionType.POP_DOCUMENT });
  const saveRecording = (videoRecording: VideoRecording) =>
    dispatch({
      type: ActionType.ADD_DOCUMENT,
      document: { ...videoRecording, submitClicked: false },
    });
  const intl = useIntl();

  /**
   * Emit error metrics and error logs generated from the react-video-recorder.
   * @param error An Error instance
   */
  /* istanbul ignore next reason: Video recording cannot be tested currently */
  const handleError = (error: Error) => {
    void Logger.fatal(
      "Unable to complete video recording due to some errors.",
      error
    );
    VIDEO_RECORDER_ERROR_PUBLISHER.publishCounterMonitor(
      "VideoRecorderError",
      1
    );
  };

  return (
    <div data-test-id="videoRecorderDataTestId">
      <VideoRecorder
        retryI18nId={"identityCheck-button-retry"}
        onRecord={saveRecording}
        onRetry={retryRecording}
        snowplowTracker={SnowplowTracker.instance}
        katalLogger={Logger.instance}
        onError={handleError}
        onErrorRenderer={(error: Error) => <RecorderError error={error} />}
        noMovementText={intl.formatMessage({
          id: "videoRecorder-noMovementText",
        })}
        personId={SnowplowTracker.userId}
        alias={state.amazonUsername}
        {...props}
        resolutionProfile={resolutionProfile}
      >
        <SubmitVideoButton
          eventCategory={props.eventCategory}
          clearSelectedStep={props.clearSelectedStep}
          fullIdVerificationElementRef={props.fullIdVerificationElementRef}
          refPage={props.refPage}
        />
      </VideoRecorder>
    </div>
  );
}
